import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { UserRole, type ModuloAdmin } from '@kick/types';
import { restaurarSesion } from '@/lib/api';
import { esAdmin, useAuth } from '@/lib/auth-store';
import { useAccesoTotal, usePuede } from '@/lib/permisos';
import { Layout } from '@/components/Layout';
import { Login } from '@/pages/Login';
import { CambiarPassword } from '@/pages/CambiarPassword';
import { Resumen } from '@/pages/Resumen';
import { Menu } from '@/pages/Menu';
import { Pedidos } from '@/pages/Pedidos';
import { Calificaciones } from '@/pages/Calificaciones';
import { Cupones } from '@/pages/Cupones';
import { Reclamos } from '@/pages/admin/Reclamos';
import { Envios } from '@/pages/Envios';
import { SoporteComercio } from '@/pages/SoporteComercio';
import { EnviosMasivos } from '@/pages/EnviosMasivos';
import { Cuenta } from '@/pages/Cuenta';
import { Cobros } from '@/pages/admin/Cobros';
import { Clasificacion } from '@/pages/admin/Clasificacion';
import { Anuncios } from '@/pages/admin/Anuncios';
import { Programadas } from '@/pages/admin/Programadas';
import { Manuales } from '@/pages/admin/Manuales';
import { Soporte } from '@/pages/admin/Soporte';
import { Usuarios } from '@/pages/Usuarios';
import { Operacion } from '@/pages/admin/Operacion';
import { Comercios } from '@/pages/admin/Comercios';
import { UsuariosAdmin } from '@/pages/admin/Usuarios';
import { Equipo } from '@/pages/admin/Equipo';
import { Domiciliarios } from '@/pages/admin/Domiciliarios';
import { Auditoria } from '@/pages/admin/Auditoria';

/**
 * El perfil se carga aparte porque arrastra MapLibre (~800 kB).
 * La mayoría de las sesiones del panel nunca abren esta pantalla: quien
 * atiende pedidos no cambia la ubicación del local todos los días.
 */
const Perfil = lazy(() => import('@/pages/Perfil').then((m) => ({ default: m.Perfil })));

/** También arrastra MapLibre, por el editor de polígonos. */
const Zonas = lazy(() => import('@/pages/Zonas').then((m) => ({ default: m.Zonas })));

/** Los ajustes no llevan mapa, pero solo los abre el admin de vez en cuando. */
const Ajustes = lazy(() => import('@/pages/admin/Ajustes').then((m) => ({ default: m.Ajustes })));

/** El mapa de flota arrastra MapLibre. */
const Flota = lazy(() => import('@/pages/admin/Flota').then((m) => ({ default: m.Flota })));

/** Los informes arrastran Recharts (~250 kB): solo quien los abre los descarga. */
const Informes = lazy(() => import('@/pages/Informes').then((m) => ({ default: m.Informes })));
const InformesAdmin = lazy(() =>
  import('@/pages/admin/InformesAdmin').then((m) => ({ default: m.InformesAdmin })),
);
const Liquidaciones = lazy(() =>
  import('@/pages/admin/Liquidaciones').then((m) => ({ default: m.Liquidaciones })),
);

export function App() {
  const { accessToken, cargando, mustChangePassword } = useAuth();

  /**
   * Al cargar la página se intenta recuperar la sesión con la cookie de
   * refresh. El access token vive en memoria y se pierde al recargar; sin
   * este paso, F5 sacaría al usuario del panel.
   */
  useEffect(() => {
    void restaurarSesion();
  }, []);

  if (cargando) return <Cargando />;

  if (!accessToken) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  /**
   * La contraseña temporal bloquea el panel entero.
   *
   * Va antes que cualquier ruta a propósito: si fuera una redirección desde
   * dentro del layout, bastaría escribir otra URL para saltársela.
   */
  if (mustChangePassword) return <CambiarPassword />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Resumen />} />

        {/* Solo administración */}
        <Route path="/comercios" element={<SoloAdmin modulo="comercios"><Comercios /></SoloAdmin>} />
        <Route
          path="/comercios/:merchantId"
          element={
            <SoloAdmin modulo="comercios">
              <Suspense fallback={<Cargando />}>
                <FichaDeComercio />
              </Suspense>
            </SoloAdmin>
          }
        />
        <Route
          path="/comercios/:merchantId/menu"
          element={<SoloAdmin modulo="comercios"><MenuDeComercio /></SoloAdmin>}
        />
        <Route path="/usuarios-plataforma" element={<SoloAdmin modulo="usuarios"><UsuariosAdmin /></SoloAdmin>} />
        <Route path="/domiciliarios" element={<SoloAdmin modulo="domiciliarios"><Domiciliarios /></SoloAdmin>} />
        <Route path="/operacion" element={<SoloAdmin modulo="operacion"><Operacion /></SoloAdmin>} />
        <Route path="/auditoria" element={<SoloAdmin modulo="auditoria"><Auditoria /></SoloAdmin>} />
        <Route path="/reclamos" element={<SoloAdmin modulo="reclamos"><Reclamos /></SoloAdmin>} />
        <Route path="/anuncios" element={<SoloAdmin modulo="anuncios"><Anuncios /></SoloAdmin>} />
        <Route path="/programadas" element={<SoloAdmin modulo="programadas"><Programadas /></SoloAdmin>} />
        <Route path="/manuales" element={<SoloAdmin modulo="manuales"><Manuales /></SoloAdmin>} />
        <Route path="/cobros" element={<SoloAdmin modulo="cobros"><Cobros /></SoloAdmin>} />
        <Route path="/clasificacion" element={<SoloAdmin modulo="clasificacion"><Clasificacion /></SoloAdmin>} />
        <Route path="/soporte" element={<SoporteSegunRol />} />
        <Route path="/equipo" element={<SoloAccesoTotal><Equipo /></SoloAccesoTotal>} />
        <Route
          path="/flota"
          element={
            <SoloAdmin modulo="domiciliarios">
              <Suspense fallback={<Cargando />}>
                <Flota />
              </Suspense>
            </SoloAdmin>
          }
        />
        <Route
          path="/informes-plataforma"
          element={
            <SoloAdmin modulo="informes">
              <Suspense fallback={<Cargando />}>
                <InformesAdmin />
              </Suspense>
            </SoloAdmin>
          }
        />
        <Route
          path="/liquidaciones"
          element={
            <SoloAdmin modulo="liquidaciones">
              <Suspense fallback={<Cargando />}>
                <Liquidaciones />
              </Suspense>
            </SoloAdmin>
          }
        />
        <Route
          path="/ajustes"
          element={
            <SoloAdmin modulo="ajustes">
              <Suspense fallback={<Cargando />}>
                <Ajustes />
              </Suspense>
            </SoloAdmin>
          }
        />
        <Route
          path="/zonas"
          element={
            <SoloAdmin modulo="zonas">
              <Suspense fallback={<Cargando />}>
                <Zonas />
              </Suspense>
            </SoloAdmin>
          }
        />

        {/* Solo comercios: el administrador no tiene un menú propio que editar. */}
        <Route path="/menu" element={<SoloComercio><Menu /></SoloComercio>} />
        <Route path="/pedidos" element={<SoloComercio><Pedidos /></SoloComercio>} />
        <Route path="/envios" element={<SoloComercio><Envios /></SoloComercio>} />
        <Route path="/envios/masivos" element={<SoloComercio><EnviosMasivos /></SoloComercio>} />
        <Route path="/cuenta" element={<SoloComercio><Cuenta /></SoloComercio>} />
        {/* La ven los dos: el comercio las suyas, administración las peores. */}
        <Route path="/calificaciones" element={<Compartida modulo="informes"><Calificaciones /></Compartida>} />
        <Route path="/cupones" element={<Compartida modulo="cupones"><Cupones /></Compartida>} />
        <Route
          path="/informes"
          element={
            <SoloComercio soloPropietario>
              <Suspense fallback={<Cargando />}>
                <Informes />
              </Suspense>
            </SoloComercio>
          }
        />
        <Route
          path="/perfil"
          element={
            <SoloComercio>
              <Suspense fallback={<Cargando />}>
                <Perfil />
              </Suspense>
            </SoloComercio>
          }
        />
        <Route
          path="/usuarios"
          element={
            <SoloComercio soloPropietario>
              <Usuarios />
            </SoloComercio>
          }
        />
      </Route>

      {/* Ya autenticado, /login no tiene sentido. */}
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="*" element={<NoEncontrado />} />
    </Routes>
  );
}

/**
 * Cierra las rutas del comercio al administrador y, si se pide, a los
 * empleados. Es comodidad de navegación: la autorización real la impone la
 * API en cada petición.
 */
function SoloComercio({
  soloPropietario,
  children,
}: {
  soloPropietario?: boolean;
  children: React.ReactNode;
}) {
  const { user, merchantId } = useAuth();

  if (esAdmin(user?.role) || !merchantId) return <Navigate to="/" replace />;
  if (soloPropietario && user?.role === UserRole.MERCHANT_STAFF) return <Navigate to="/" replace />;

  return <>{children}</>;
}

/**
 * Comodidad de navegación: la autorización real la impone la API.
 *
 * Con `modulo`, además del rol se exige el permiso «ver» de ese módulo: un
 * administrador restringido que escriba la URL a mano vuelve al resumen, igual
 * que si hubiera entrado por el menú, donde ese enlace no existe.
 */
function SoloAdmin({ modulo, children }: { modulo?: ModuloAdmin; children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  const puede = usePuede();

  if (!esAdmin(user?.role)) return <Navigate to="/" replace />;
  if (modulo && !puede(modulo, 'ver')) return <Navigate to="/" replace />;

  return <>{children}</>;
}

/** El equipo de administración lo gestiona solo quien tiene acceso total. */
function SoloAccesoTotal({ children }: { children: React.ReactNode }) {
  const accesoTotal = useAccesoTotal();
  return accesoTotal ? <>{children}</> : <Navigate to="/" replace />;
}

/** La ficha de un comercio abierta por el administrador: la misma pantalla del comerciante. */
function FichaDeComercio() {
  const { merchantId } = useParams<{ merchantId: string }>();
  return <Perfil merchantId={merchantId} />;
}

function MenuDeComercio() {
  const { merchantId } = useParams<{ merchantId: string }>();
  return <Menu merchantId={merchantId} />;
}

/** Ruta que comparten comercio y administración: al administrador se le exige el módulo. */
/** Soporte tiene dos caras: la bandeja de administración y el chat del comercio. */
function SoporteSegunRol() {
  const user = useAuth((s) => s.user);
  return esAdmin(user?.role) ? (
    <SoloAdmin modulo="soporte">
      <Soporte />
    </SoloAdmin>
  ) : (
    <SoloComercio>
      <SoporteComercio />
    </SoloComercio>
  );
}

function Compartida({ modulo, children }: { modulo: ModuloAdmin; children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  const puede = usePuede();

  if (esAdmin(user?.role) && !puede(modulo, 'ver')) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function Cargando() {
  return (
    <div className="grid min-h-dvh place-items-center bg-kick-light">
      <div className="flex flex-col items-center gap-3">
        <img src="/logo.png" alt="Kick Domicilios" className="h-14 w-auto opacity-90" />
        <Loader2 className="h-6 w-6 animate-spin text-kick-red" />
      </div>
    </div>
  );
}

function NoEncontrado() {
  return (
    <div className="grid min-h-dvh place-items-center bg-kick-light px-4 text-center">
      <div>
        <p className="font-display text-7xl text-kick-red">404</p>
        <h1 className="mt-2 text-2xl text-kick-dark">Página no encontrada</h1>
        <a href="/" className="btn-kick mt-6">
          Volver al panel
        </a>
      </div>
    </div>
  );
}
